import { defineStore } from 'pinia'
import { client } from '@/services/client'
import type { Partner, PartnerResponse } from '@/types'
import { toggleUnleashPartner } from '@/utils/setup-application/createUnleash'
import type { PartnerSignupPayload } from '@/components/Auth/Partner/types'

export type State = {
  partner: Partner
  loadingPartnerPromise: Promise<Partner> | null
}

const state = (): State => ({
  partner: {} as Partner,
  loadingPartnerPromise: null,
})

const getters = {
  getPartnerInfo: (state: State): Partner => state.partner,
  hasPartner: (state: State): boolean =>
    state.partner && Object.keys(state.partner).length > 0,
}

const actions = {
  setPartnerInfo(value): void {
    this.partner = value
  },

  clearPartnerInfo(): void {
    this.partner = {}
  },
  setLoadingPartnerPromise(value): void {
    this.loadingPartnerPromise = value
  },

  signUpPartner(payload: PartnerSignupPayload): Promise<PartnerResponse> {
    return client.post('/partners', {
      partner: payload,
    })
  },

  async loadPartnerInfo(): Promise<Partner> {
    try {
      if (this.loadingPartnerPromise) {
        return this.loadingPartnerPromise
      }

      const promise = client.get('/partners/me')

      this.setLoadingPartnerPromise(promise)

      const res = await promise

      this.setLoadingPartnerPromise(null)

      const { partner } = res.data
      toggleUnleashPartner(partner?.id)
      this.setPartnerInfo(partner)
      return partner
    } catch (err) {
      this.setLoadingPartnerPromise(null)
      return Promise.reject(err)
    }
  },

  updatePartnerInfo(payload): Promise<PartnerResponse> {
    const { partner } = payload
    return client.put('/partners/me', {
      partner,
    })
  },
}

export const usePartnerUser = defineStore('partnerUser', {
  state,
  getters,
  actions,
})

export default usePartnerUser
